import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackClairtyEvent } from '../utils';

export function usePostSeenTracker({ sendSeenPosts, threshold = 0.5 }) {
  const postSeenRef = useRef(new Set());
  const observerRef = useRef(null);
  const location = useLocation(); // current route path

  const getRefForPost = postId => node => {
    if (!node) return;

    node.dataset.postid = postId;
    observerRef.current?.observe(node);
  };

  // Init IntersectionObserver
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const postId = entry.target.dataset.postid;
          if (entry.isIntersecting && postId && !postSeenRef.current.has(postId)) {
            trackClairtyEvent('readreview', {postId});
            postSeenRef.current.add(postId);
            observerRef.current.unobserve(entry.target);
          }
        });
      },
      { threshold },
    );

    return () => observerRef.current?.disconnect();
  }, [threshold]);

  // Send postSeen
  const sendPostSeen = () => {
    const seenPostIds = Array.from(postSeenRef.current);
    if (seenPostIds.length === 0) return;

    try {
      const blob = new Blob([JSON.stringify({ postIds: seenPostIds })], {
        type: 'application/json',
      });
      navigator.sendBeacon(sendSeenPosts.url, blob);
    } catch (err) {
      sendSeenPosts.fallback({ postIds: seenPostIds });
    }

    postSeenRef.current.clear();
  };

  // On tab close / refresh
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') sendPostSeen();
    };
    const handleBeforeUnload = () => sendPostSeen();

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return { getRefForPost, sendPostSeen };
}
